<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modalTitle"
    @on-confirm="saveAlerts"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    maxWidth="60%"
  >
    <v-row v-if="alerts.length == 0" justify="center" align="center">
      <v-col cols="1">
        <v-btn
          tile
          color="primary"
          prepend-icon="mdi-plus"
          outlined
          @click="addAlert()"
        >
          {{ $t("Add") }}
        </v-btn>
      </v-col>
    </v-row>
    <div v-for="(item, index) in alerts" :key="item.uuid">
      <v-row align="center">
        <v-col cols="1">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                tile
                large
                color="error"
                icon
                @click="removeAlert(item.uuid)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Delete") }}</span>
          </v-tooltip>
        </v-col>
        <v-col>
          <v-text-field
            readonly
            v-model="item.displayConditionStr"
            :label="$t('Condition')"
            :append-icon="item.conditionStr == '' ? 'mdi-plus' : 'mdi-pencil'"
            @click:append="editCondition(item)"
            :error-messages="conditionErrors(index)"
          ></v-text-field>
        </v-col>
        <v-col>
          <v-select
            :label="$t('Roles')"
            v-model="item.roleTypes"
            :items="rolesList"
            item-text="text"
            item-value="id"
            multiple
            :error-messages="roleErrors(index)"
          ></v-select>
        </v-col>
        <v-col>
          <v-select
            :label="$t('Frequency')"
            v-model="item.triggerId"
            :items="triggerList"
            item-text="text"
            item-value="id"
            :error-messages="triggerErrors(index)"
          ></v-select>
        </v-col>
        <v-col>
          <v-btn color="blue darken-4" outlined @click="editActions(item)">
            {{ $t("Actions") }}
            <v-icon v-if="item.actions.length > 0" right> mdi-pencil </v-icon>
            <v-icon v-if="item.actions.length == 0" right> mdi-plus </v-icon>
          </v-btn>
        </v-col>
        <v-col>
          <v-switch
            v-model="item.notifyByEmail"
            :label="$t('NotifyByEmail')"
          ></v-switch>
        </v-col>
        <v-col cols="1">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                tile
                large
                color="primary"
                icon
                @click="addAlert()"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Add") }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </div>
    <template v-slot:actions>
      <div style="position: absolute">
        <v-menu bottom right>
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  text
                  small
                  v-bind="attrs"
                  v-on="{ ...tooltip, ...menu }"
                  color="blue-grey"
                >
                  <v-icon large>mdi-history</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("Versions") }}</span>
            </v-tooltip>
          </template>
          <v-list>
            <v-list-item
              v-for="(item, i) in versionList"
              :key="i"
              @click="onVersionChange(item)"
            >
              <v-list-item-title>{{ "v" + item.text }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </template>
    <SearchCriteriaBuilderModal
      :ref="conditionModalKey"
      :contentTypeIDs="[contentType.id]"
      @on-condition-saved="onConditionChanged"
    />
    <ActionsModal
      :ref="actionsModalKey"
      :contentTypeId="contentType.id"
      :contentTypeFields="contentType.fields"
      @on-saved="onActionsSaved"
    />
  </base-modal>
</template>
<script>
import SearchCriteriaBuilderModal from "@/components/search/SearchCriteriaBuilderModal.vue";
import Utils from "@/ecm/common/Utils";
import ActionsModal from "@/components/administration/contentType/alerts/ActionsModal.vue";
import ContentType from "@/ecm/models/contentType/ContentType";
import ContentTypeField from "@/ecm/models/contentType/ContentTypeField";
import ContentTypeAlert from "@/ecm/models/contentType/alerts/ContentTypeAlert";
import ContentTypeService from "@/services/ContentTypeService";
import ContentTypeAlertsTriggers from "@/ecm/variables/ContentTypeAlertsTriggers";
import RoleService from "@/services/RoleService";
import { required, requiredIf } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";

export default {
  components: {
    SearchCriteriaBuilderModal,
    ActionsModal,
  },
  mounted() {
    this.getRoles();
    this.$v.$reset();
  },
  data() {
    return {
      modal: {
        title: this.$t("Alerts"),
        key: "AlertsModal",
      },
      contentType: new ContentType(),
      open: false,
      conditionModalOpen: false,
      triggerList: [
        {
          id: ContentTypeAlertsTriggers.Periodically,
          text: this.$t("Periodically"),
        },
        {
          id: ContentTypeAlertsTriggers.OnDocumentCreation,
          text: this.$t("NewDocument"),
        },
        {
          id: ContentTypeAlertsTriggers.OnNewDocumentVersion,
          text: this.$t("NewVersion"),
        },
      ],
      currentItem: new ContentTypeAlert(),
      contentTypeRequiredFieldsCodes: [],
      alerts: [],
      versionList: [],
      rolesList: [],
      conditionModalKey: "conditionModalKey",
      actionsModalKey: "actionModalKey",
    };
  },
  mixins: [validationMixin],
  validations: {
    alerts: {
      $each: {
        conditionStr: { required },
        roleTypes: {
          required: requiredIf(function (model) {
            return model.actions.length == 0;
          }),
        },
        triggerId: { required },
      },
    },
  },
  computed: {
    modalTitle() {
      return `${this.modal.title} - ${this.contentType.name} ${this.contentType.version}`;
    },
    modalInstance() {
      return this.$refs[this.modal.key];
    },
    conditionModalInstance() {
      return this.$refs[this.conditionModalKey];
    },
    actionsModalInstance() {
      return this.$refs[this.actionsModalKey];
    },
  },
  methods: {
    conditionErrors(index) {
      const errors = [];
      if (!this.$v.alerts.$each[index].conditionStr.$dirty) return errors;
      !this.$v.alerts.$each[index].conditionStr.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    roleErrors(index) {
      const errors = [];
      if (!this.$v.alerts.$each[index].roleTypes.$dirty) return errors;
      !this.$v.alerts.$each[index].roleTypes.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    triggerErrors(index) {
      const errors = [];
      if (!this.$v.alerts.$each[index].triggerId.$dirty) return errors;
      !this.$v.alerts.$each[index].triggerId.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    edit(dto) {
      this.contentType = new ContentType(dto);
      this.getAlerts();
      this.getVersions();
      this.getFields();
      this.show();
      
    },
    editCondition(item) {
      this.currentItem = item;
      this.conditionModalInstance.edit(item.conditionStr);
    },
    onConditionChanged(condition) {
      this.currentItem.conditionStr = condition.conditionStr;
      this.currentItem.displayConditionStr = condition.displayConditionStr;
    },
    editActions(item) {
      this.currentItem = item;
      this.actionsModalInstance.edit(item.actions);
    },
    onActionsSaved(actions) {
      this.currentItem.actions = actions;
    },
    onVersionChange(version) {
      if (this.contentType.id != version.id) {
        this.contentType.id = parseInt(version.id);
        this.contentType.version = "v" + version.text;
        this.getAlerts();
        this.getFields();
      }
    },
    getAlerts() {
      ContentTypeService.getAlerts(this.contentType.id).then((data) => {
        this.alerts = data.map((dto) => new ContentTypeAlert(dto));
      });
    },
    getVersions() {
      ContentTypeService.getVersions(this.contentType.id).then((data) => {
        this.versionList = data;
      });
    },
    getFields() {
      ContentTypeService.getFields([this.contentType.id]).then((data) => {
        this.contentType.fields = data.map(function (dto) {
          return new ContentTypeField(dto);
        });
      });
    },
    getRoles() {
      RoleService.getList().then((data) => {
        this.rolesList = data;
      });
    },
    saveAlerts() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      
      var requiredFieldCodes=this.contentType.fields.filter((field)=>field.required).map((item)=>item.code)
      if (requiredFieldCodes.length > 0) {
        var alertsWithEmptyRequiredFields = this.alerts
          .filter((alert) =>
            alert.actions.some(
              (field) =>field.defFieldCode!=""&&
                requiredFieldCodes.includes(
                  field.defFieldCode
                ) && field.value== ""
            )
          )
          .map((item) => item.displayConditionStr);
        if (alertsWithEmptyRequiredFields.length > 0) {
          var errorMessage = alertsWithEmptyRequiredFields.join(",");
          Utils.toast.error(
            this.$t("PendingNullFieldsAlerts") + errorMessage
          );
          return;
        }
      }
      this.modalInstance.progress(true);
      var alerts = this.alerts.map((item) => item.toDto());
      ContentTypeService.saveAlerts(this.contentType.id, alerts)
        .then((data) => {
          this.$emit("on-alerts-saved", this.alerts);
          this.hide();
        })
        .catch((error) => {
          // error
        })
        .then(() => {
          //always
          this.modalInstance.progress(false);
        });
    },
    addAlert() {
      this.alerts.push(new ContentTypeAlert());
    },
    removeAlert(uuid) {
      this.alerts = this.alerts.filter((item) => item.uuid !== uuid);
    },
    show() {
      this.open = true;
    },
    hide() {
      this.open = false;
    },
  },
};
</script>
